<script setup lang="ts">
  import { computed, type PropType } from 'vue'

  import { useI18n } from 'vue-i18n'

  import { MaterialVideoLibraryIcon } from '@gohighlevel/ghl-icons/24/material/rounded'

  import { createElementId } from '@common/core-layer/utils/core.utils'

  import { convertToRelativeTime } from '@common/core-layer/utils/date.utils'

  import OptimisedImage from '@common/media-layer/components/OptimisedImage.vue'

  import { useCPNavigation } from '@common/navigation-layer/composables/useNavigationContext'

  import courseLogoFallback from '@common/shared-ui-layer/assets/course-logo.webp'

  import CPText from '@ui/components/CPText.vue'

  import type { CourseResult } from '@courses/types-layer/types/search'

  const props = defineProps({
    course: {
      type: Object as PropType<CourseResult>,
      required: true,
    },
    variant: {
      type: String as PropType<'compact' | 'full'>,
      default: 'compact',
    },
  })

  const { CPNavigateTo } = useCPNavigation()
  const { t } = useI18n()

  const progressText = computed(() => {
    const completed = props.course.completedPosts ?? 1
    const total = props.course.totalPosts ?? 5
    return `${completed}/${total} ${t('courses.lessons')}`
  })

  const metadataText = computed(() => {
    const parts: string[] = []

    if (props.course.author) {
      parts.push(`${t('courses.productCard.by')} ${props.course.author}`)
    }

    if (props.course.membersCount != null) {
      const count = props.course.membersCount >= 1000 ? `${(props.course.membersCount / 1000).toFixed(1)}K` : props.course.membersCount
      parts.push(t('courses.search.searchResults.members', { count }))
    }

    if (props.course.updatedAt) {
      parts.push(convertToRelativeTime(props.course.updatedAt))
    }

    return parts.join(' • ')
  })

  const handleClick = async () => {
    // Navigate to the product/course detail page
    // Pass true to force default courses route (not communities learning route)
    await CPNavigateTo(
      {
        name: 'courses-products',
        params: { productId: props.course._id },
      },
      true
    )
  }
</script>

<template>
  <!-- eslint-disable enforce-rem-spacing/rem-spacing -- pre-existing card dimensions -->
  <!-- Compact Variant (Original) -->
  <div
    v-if="variant === 'compact'"
    :id="createElementId('search-result', 'course-card', course._id)"
    class="w-[160px] flex-shrink-0 cursor-pointer"
    role="button"
    tabindex="0"
    @click="handleClick"
    @keydown.enter="handleClick"
  >
    <!-- Course Image with Play Button -->
    <div class="relative mb-3 aspect-video overflow-hidden rounded-2xl bg-surface">
      <OptimisedImage
        v-if="course.posterImage"
        :src="course.posterImage"
        :alt="course.title"
        source="courses"
        gradient-mode="always"
        fallback-aspect="aspect-video"
        loading="eager"
        fetchpriority="high"
      />

      <!-- Progress Badge -->
      <div class="absolute right-2 top-2 z-10 flex items-center gap-1.5 rounded-full bg-background p-1 shadow-sm">
        <MaterialVideoLibraryIcon class="h-4 w-4 text-body" />
        <CPText size="xs" weight="medium" class="text-body">
          {{ progressText }}
        </CPText>
      </div>
    </div>

    <!-- Course Info -->
    <div class="flex items-center gap-2">
      <img
        :src="courseLogoFallback"
        :alt="course.title"
        class="border-1 h-[32px] w-[32px] rounded-full border-solid border-default object-cover"
      />

      <div class="min-w-0 flex-1">
        <CPText size="2xl" weight="semibold" class="line-clamp-1 text-body">
          {{ course.title }}
        </CPText>
      </div>
    </div>
  </div>

  <!-- Full Variant -->
  <div
    v-else
    :id="createElementId('search-result', 'course-card', course._id)"
    class="w-full cursor-pointer"
    role="button"
    tabindex="0"
    @click="handleClick"
    @keydown.enter="handleClick"
  >
    <!-- Course Image (No Border Radius) -->
    <div class="relative mb-3 aspect-video overflow-hidden bg-surface">
      <OptimisedImage
        v-if="course.posterImage"
        :src="course.posterImage"
        :alt="course.title"
        source="courses"
        gradient-mode="always"
        fallback-aspect="aspect-video"
        loading="eager"
        fetchpriority="high"
      />

      <!-- Progress Badge -->
      <div class="absolute right-3 top-3 z-10 flex items-center gap-1.5 rounded-full bg-background px-2.5 py-1.5 shadow-sm">
        <MaterialVideoLibraryIcon class="h-4 w-4 text-body" />
        <CPText size="sm" weight="medium" class="text-body">
          {{ progressText }}
        </CPText>
      </div>
    </div>

    <!-- Course Info -->
    <div class="flex items-start gap-3">
      <img :src="courseLogoFallback" :alt="course.title" class="h-10 w-10 flex-shrink-0 rounded-full object-cover" />

      <div class="min-w-0 flex-1">
        <CPText size="lg" weight="semibold" class="line-clamp-1 text-body">
          {{ course.title }}
        </CPText>
        <CPText v-if="metadataText" :id="createElementId('search-result', 'course-metadata', course._id)" size="sm" class="text-secondary">
          {{ metadataText }}
        </CPText>
      </div>
    </div>
  </div>
</template>
